import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Download, Eye, Truck, FileText, Building2, Repeat, Pause, Play, Square } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg, inr, fmtDate, today, openFile } from "@/lib/api";
import { PageHeader, StatCard, Field, TableWrap, Empty, th, td, tr } from "@/components/common";
import { DocUpload, downloadCsv } from "@/components/market";

const MODES = ["Cash", "UPI", "Bank Transfer", "Credit Card"];
const COLORS = ["#2563eb", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#84cc16", "#64748b"];
const nowTime = () => new Date().toTimeString().slice(0, 5);
const EMPTY = { date: today(), time: nowTime(), category: "repairs", amount: "", payment_mode: "Cash", receipt_file_id: null, allocation: "overhead", booking_id: "", vehicle_id: "", note: "", recurring: false, recurring_until: "" };

function ExpenseDialog({ open, initial, cats, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [lrs, setLrs] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [lrQ, setLrQ] = useState("");
  useEffect(() => {
    if (!open) return;
    setF(initial ? { ...EMPTY, ...initial, booking_id: initial.booking_id || "", vehicle_id: initial.vehicle_id || "" } : { ...EMPTY, time: nowTime() });
    setLrQ("");
    api.get("/bookings").then((r) => setLrs(r.data)).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, [open, initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const lrList = lrs.filter((b) => !lrQ || `${b.lr_number} ${b.vehicle_number || ""} ${b.pickup_location} ${b.drop_location}`.toLowerCase().includes(lrQ.toLowerCase())).slice(0, 100);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, amount: Number(f.amount) || 0, booking_id: f.booking_id || null, vehicle_id: f.vehicle_id || null };
      initial ? await api.put(`/central-expenses/${initial.id}`, body) : await api.post("/central-expenses", body);
      toast.success(initial ? "Expense updated" : "Expense added");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto bg-card" data-testid="central-expense-dialog">
        <DialogHeader><DialogTitle>{initial ? "Edit Expense" : "Add Expense"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
          <Field label="Date *"><Input type="date" required data-testid="ce-date-input" value={f.date} onChange={set("date")} /></Field>
          <Field label="Time"><Input type="time" data-testid="ce-time-input" value={f.time} onChange={set("time")} /></Field>
          <Field label="Category *">
            <select data-testid="ce-category-select" className="native-select w-full" value={f.category} onChange={set("category")}>
              {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Amount (₹) *"><Input type="number" min="1" step="0.01" required data-testid="ce-amount-input" value={f.amount} onChange={set("amount")} /></Field>
          <Field label="Payment Mode">
            <select data-testid="ce-mode-select" className="native-select w-full" value={f.payment_mode} onChange={set("payment_mode")}>
              {MODES.map((m) => <option key={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="Bill / Receipt"><DocUpload label="Bill" refType="document" value={f.receipt_file_id} onChange={set("receipt_file_id")} testid="ce-bill-upload" /></Field>
          <div className="sm:col-span-2">
            <div className="label-xs mb-2">Allocate To</div>
            <div className="grid grid-cols-3 gap-2" data-testid="ce-allocation-toggle">
              {[["trip", "Trip / LR", FileText], ["vehicle", "Vehicle", Truck], ["overhead", "Company Overhead", Building2]].map(([v, l, I]) => (
                <button key={v} type="button" data-testid={`ce-alloc-${v}`} onClick={() => setF({ ...f, allocation: v })}
                  className={`flex h-10 items-center justify-center gap-1.5 rounded-lg border text-xs font-semibold transition-colors sm:text-sm ${f.allocation === v ? "border-primary bg-primary/10 text-primary" : "text-muted-foreground hover:border-ring"}`}>
                  <I className="h-4 w-4" />{l}
                </button>
              ))}
            </div>
          </div>
          {f.allocation === "trip" && <>
            <Field label="Search LR"><Input data-testid="ce-lr-search" value={lrQ} onChange={(e) => setLrQ(e.target.value)} placeholder="LR no., vehicle, city" /></Field>
            <Field label="Trip / LR *">
              <select required data-testid="ce-lr-select" className="native-select w-full" value={f.booking_id} onChange={set("booking_id")}>
                <option value="">Select LR…</option>
                {lrList.map((b) => <option key={b.id} value={b.id}>{`${b.lr_number} · ${b.vehicle_number || "-"} · ${b.pickup_location} → ${b.drop_location}`}</option>)}
              </select>
            </Field>
          </>}
          {f.allocation === "vehicle" && (
            <Field label="Vehicle *" className="sm:col-span-2">
              <select required data-testid="ce-vehicle-select" className="native-select w-full" value={f.vehicle_id} onChange={set("vehicle_id")}>
                <option value="">Select vehicle…</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.vehicle_number}</option>)}
              </select>
            </Field>
          )}
          <Field label="Note" className="sm:col-span-2"><Input data-testid="ce-note-input" value={f.note} onChange={set("note")} placeholder="e.g. HDFC EMI Oct, Office rent" /></Field>
          {f.allocation !== "trip" && !initial?.auto && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed p-3 sm:col-span-2" data-testid="ce-recurring-box">
              <label className="flex items-center gap-2 text-sm font-semibold">
                <Switch data-testid="ce-recurring-switch" checked={!!f.recurring} onCheckedChange={(v) => setF({ ...f, recurring: v })} />
                <Repeat className="h-4 w-4 text-primary" />Har mahine repeat karein
              </label>
              {f.recurring && <>
                <span className="text-xs text-muted-foreground">every month on day {Number(f.date?.slice(8, 10)) || "-"} · till</span>
                <Input type="month" className="h-8 w-40" data-testid="ce-recurring-until" value={f.recurring_until} onChange={set("recurring_until")} />
                <span className="text-[11px] text-muted-foreground">(blank = no end)</span>
              </>}
            </div>
          )}
          <p className="text-[11px] text-muted-foreground sm:col-span-2">Lump-sum advance given to a driver is not an expense here — record it in Driver Settlement. Driver's approved trip bills appear automatically.</p>
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="ce-save-btn" disabled={busy}>{initial ? "Save Changes" : "Add Expense"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AllocCell({ r }) {
  if (r.allocation === "trip") return <Link to={`/bookings/${r.booking_id}`} className="font-mono text-xs font-semibold text-primary hover:underline">{r.lr_number}</Link>;
  if (r.allocation === "vehicle") return <span className="font-mono text-xs">{r.vehicle_number}</span>;
  return <span className="text-xs text-muted-foreground">Company Overhead</span>;
}

function ExpenseTable({ rows, onEdit, onDelete, testid }) {
  return (
    <>
      <TableWrap>
        <thead><tr>{["Date", "Category", "Allocated To", "Vehicle", "Mode", "Note", "Amount", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.source}-${r.id}`} className={tr} data-testid={`${testid}-row-${r.id}`}>
              <td className={`${td} whitespace-nowrap`}>{fmtDate(r.date)}{r.time && <div className="text-[10px] text-muted-foreground">{r.time}</div>}</td>
              <td className={td}>{r.category_label}{r.auto && <span className="ml-1.5 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-bold text-sky-700 dark:bg-sky-950 dark:text-sky-400" data-testid={`${testid}-auto-${r.id}`}>Auto</span>}{r.recurring && <Repeat className="ml-1.5 inline h-3.5 w-3.5 text-primary" />}{r.sub_type && <div className="text-[10px] text-muted-foreground">Driver bill · {r.sub_type}</div>}</td>
              <td className={td}><AllocCell r={r} /></td>
              <td className={`${td} font-mono text-xs`}>{r.vehicle_number || "-"}</td>
              <td className={`${td} text-xs`}>{r.payment_mode}</td>
              <td className={`${td} max-w-[220px] truncate text-xs`}>{r.note || "-"}</td>
              <td className={`${td} amount font-semibold`}>{inr(r.amount, 2)}</td>
              <td className={td}>
                <div className="flex justify-end gap-1">
                  {r.receipt_file_id && <Button size="icon" variant="ghost" data-testid={`${testid}-bill-${r.id}`} onClick={() => openFile(r.receipt_file_id).catch((e) => toast.error(errMsg(e)))}><Eye className="h-4 w-4" /></Button>}
                  {r.source === "central" ? <>
                    <Button size="icon" variant="ghost" data-testid={`${testid}-edit-${r.id}`} onClick={() => onEdit(r)}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" data-testid={`${testid}-delete-${r.id}`} onClick={() => onDelete(r)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                  </> : <Link to={`/bookings/${r.booking_id}`} className="px-2 text-[11px] font-semibold text-muted-foreground hover:text-primary">Edit on LR</Link>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      {!rows.length && <Empty testid={`${testid}-empty`} text="No expenses in this period" />}
    </>
  );
}

const csvRows = (rows) => rows.map((r) => [r.date, r.time, r.category_label, r.sub_type || "", r.allocation === "trip" ? r.lr_number : r.allocation === "vehicle" ? "Vehicle" : "Overhead", r.vehicle_number || "", r.payment_mode, r.note || "", r.amount, r.source === "central" ? "Central" : "Driver bill"]);
const CSV_HEAD = ["Date", "Time", "Category", "Sub Type", "Allocated To", "Vehicle", "Mode", "Note", "Amount", "Source"];

function VehicleView({ data, cats }) {
  const keys = cats.map((c) => c.key);
  const csv = () => downloadCsv(`Vehicle_Expenses.csv`, ["Vehicle", ...cats.map((c) => c.label), "Total Cost", "Trips", "Revenue", "Net"],
    data.map((v) => [v.vehicle_number, ...keys.map((k) => v.breakdown[k] || 0), v.total, v.trips, v.revenue, v.net]));
  return (
    <>
      <div className="mb-3 flex justify-end"><Button size="sm" variant="outline" data-testid="ce-vehicle-csv-btn" disabled={!data.length} onClick={csv}><Download className="mr-1 h-4 w-4" />Excel / CSV</Button></div>
      <TableWrap>
        <thead><tr>{["Vehicle", "EMI", "AMC", "RTO", "Repairs", "Insurance", "Fuel", "Trip", "Purchases", "Other", "Total Cost", "Revenue", "Net"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>
          {data.map((v) => (
            <tr key={v.vehicle_id} className={tr} data-testid={`ce-vehicle-row-${v.vehicle_number}`}>
              <td className={`${td} font-mono font-semibold`}>{v.vehicle_number}<div className="text-[10px] font-normal text-muted-foreground">{v.trips} trips</div></td>
              {["vehicle_emi", "amc", "rto", "repairs", "insurance", "fuel", "trip", "purchases"].map((k) => <td key={k} className={`${td} amount text-xs`}>{v.breakdown[k] ? inr(v.breakdown[k]) : "-"}</td>)}
              <td className={`${td} amount text-xs`}>{inr((v.breakdown.other || 0) + (v.breakdown.office || 0))}</td>
              <td className={`${td} amount font-semibold`}>{inr(v.total)}</td>
              <td className={`${td} amount`}>{inr(v.revenue)}</td>
              <td className={`${td} amount font-semibold ${v.net >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid={`ce-vehicle-net-${v.vehicle_number}`}>{inr(v.net)}</td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      {!data.length && <Empty testid="ce-vehicle-empty" text="No vehicle-tagged expenses" />}
    </>
  );
}

function TripView({ data }) {
  const csv = () => downloadCsv(`Trip_Expenses.csv`, ["LR", "Route", "Vehicle", "Status", "Freight", "Central Expenses", "Driver Bills", "Total"],
    data.map((t) => [t.lr_number, t.route, t.vehicle_number || "", t.status, t.freight, t.central, t.driver_bills, t.total]));
  return (
    <>
      <div className="mb-3 flex justify-end"><Button size="sm" variant="outline" data-testid="ce-trip-csv-btn" disabled={!data.length} onClick={csv}><Download className="mr-1 h-4 w-4" />Excel / CSV</Button></div>
      <TableWrap>
        <thead><tr>{["LR", "Route", "Vehicle", "Status", "Freight", "Central Exp.", "Driver Bills", "Total Expense"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>
          {data.map((t) => (
            <tr key={t.booking_id} className={tr} data-testid={`ce-trip-row-${t.lr_number}`}>
              <td className={td}><Link to={`/bookings/${t.booking_id}`} className="font-mono font-semibold text-primary hover:underline">{t.lr_number}</Link></td>
              <td className={td}>{t.route}</td>
              <td className={`${td} font-mono text-xs`}>{t.vehicle_number || "-"}</td>
              <td className={`${td} text-xs`}>{t.status}</td>
              <td className={`${td} amount`}>{inr(t.freight)}</td>
              <td className={`${td} amount`}>{inr(t.central)}</td>
              <td className={`${td} amount`}>{inr(t.driver_bills)}</td>
              <td className={`${td} amount font-semibold`}>{inr(t.total)}</td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      {!data.length && <Empty testid="ce-trip-empty" text="No trip expenses" />}
    </>
  );
}

function CategoryView({ data, total }) {
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="card-base h-80 p-5" data-testid="ce-category-chart">
        {data.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="amount" nameKey="label" innerRadius={70} outerRadius={110} paddingAngle={2}>
                {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => inr(v)} />
            </PieChart>
          </ResponsiveContainer>
        ) : <Empty text="No data" />}
      </div>
      <div className="card-base p-5" data-testid="ce-category-table">
        {data.map((c, i) => (
          <div key={c.key} className="flex items-center gap-3 border-b py-2.5 text-sm last:border-0" data-testid={`ce-cat-${c.key}`}>
            <span className="h-3 w-3 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
            <span className="flex-1">{c.label}</span>
            <span className="text-xs text-muted-foreground">{total ? ((c.amount / total) * 100).toFixed(1) : 0}%</span>
            <span className="amount w-28 text-right font-semibold">{inr(c.amount)}</span>
          </div>
        ))}
        <div className="flex justify-between pt-3 font-bold"><span>Total</span><span className="amount">{inr(total)}</span></div>
      </div>
    </div>
  );
}

function RecurringView({ data, onEdit, onAction }) {
  return (
    <>
      <TableWrap>
        <thead><tr>{["Started", "Category", "Allocated To", "Amount", "Every Month On", "Till", "Next Due", "Generated", "Status", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>
          {data.map((t) => (
            <tr key={t.id} className={tr} data-testid={`ce-recurring-row-${t.id}`}>
              <td className={td}>{fmtDate(t.date)}</td>
              <td className={td}>{t.category_label}<div className="text-[10px] text-muted-foreground">{t.note}</div></td>
              <td className={`${td} text-xs`}>{t.allocation === "vehicle" ? <span className="font-mono">{t.vehicle_number}</span> : "Company Overhead"}</td>
              <td className={`${td} amount font-semibold`}>{inr(t.amount, 2)}</td>
              <td className={td}>Day {t.day}</td>
              <td className={td}>{t.recurring_until || "No end"}</td>
              <td className={td} data-testid={`ce-recurring-next-${t.id}`}>{t.next_due ? fmtDate(t.next_due) : "-"}</td>
              <td className={td}>{t.generated}</td>
              <td className={td}><span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${t.recurring_active ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400" : "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400"}`} data-testid={`ce-recurring-status-${t.id}`}>{t.recurring_active ? "Active" : "Paused"}</span></td>
              <td className={td}>
                <div className="flex justify-end gap-1">
                  <Button size="icon" variant="ghost" title="Edit" data-testid={`ce-recurring-edit-${t.id}`} onClick={() => onEdit(t)}><Pencil className="h-4 w-4" /></Button>
                  {t.recurring_active
                    ? <Button size="icon" variant="ghost" title="Pause" data-testid={`ce-recurring-pause-${t.id}`} onClick={() => onAction(t, "pause")}><Pause className="h-4 w-4" /></Button>
                    : <Button size="icon" variant="ghost" title="Resume" data-testid={`ce-recurring-resume-${t.id}`} onClick={() => onAction(t, "resume")}><Play className="h-4 w-4" /></Button>}
                  <Button size="icon" variant="ghost" title="Stop repeating" data-testid={`ce-recurring-stop-${t.id}`} onClick={() => window.confirm("Stop repeating this expense? Past entries stay.") && onAction(t, "stop")}><Square className="h-4 w-4 text-rose-600" /></Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      {!data.length && <Empty testid="ce-recurring-empty" text="No recurring expenses — switch on 'Har mahine repeat karein' while adding EMI, rent or AMC" />}
    </>
  );
}

export default function CentralExpenses() {
  const [month, setMonth] = useState(today().slice(0, 7));
  const [cats, setCats] = useState([]);
  const [d, setD] = useState(null);
  const [catFilter, setCatFilter] = useState("");
  const [dlg, setDlg] = useState({ open: false, initial: null });
  const range = useMemo(() => {
    if (!month) return {};
    const [y, m] = month.split("-").map(Number);
    return { date_from: `${month}-01`, date_to: `${month}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}` };
  }, [month]);
  const load = () => api.get("/central-expenses/overview", { params: range }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { api.get("/central-expenses/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [range]); // eslint-disable-line react-hooks/exhaustive-deps
  const del = async (r) => {
    if (!window.confirm(`Delete ${r.category_label} expense of ${inr(r.amount)}?`)) return;
    try { await api.delete(`/central-expenses/${r.id}`); toast.success("Expense deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const recAction = async (t, action) => {
    try { await api.patch(`/central-expenses/${t.id}/recurring`, { action }); toast.success(action === "stop" ? "Stopped repeating" : action === "pause" ? "Paused" : "Resumed"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const allRows = d.rows.filter((r) => !catFilter || r.category === catFilter);
  const tableProps = { onEdit: (r) => setDlg({ open: true, initial: r }), onDelete: del };
  const csvBtn = (rows, name, testid) => <Button size="sm" variant="outline" data-testid={testid} disabled={!rows.length} onClick={() => downloadCsv(`${name}_${month || "all"}.csv`, CSV_HEAD, csvRows(rows))}><Download className="mr-1 h-4 w-4" />Excel / CSV</Button>;
  return (
    <div>
      <PageHeader title="Finance & Central Expenses" subtitle="फाइनेंस और कुल खर्च — all company expenses in one place">
        <Input type="month" className="w-44" data-testid="ce-month-filter" value={month} onChange={(e) => setMonth(e.target.value)} />
        <Button variant="outline" data-testid="ce-all-time-btn" onClick={() => setMonth("")}>All time</Button>
        <Button data-testid="add-central-expense-btn" onClick={() => setDlg({ open: true, initial: null })}><Plus className="mr-1 h-4 w-4" />Add Expense</Button>
      </PageHeader>
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Total Expenses" value={inr(d.totals.total)} testid="ce-total" />
        <StatCard label="Monthly Overhead" value={inr(d.totals.overhead)} accent="text-amber-600" testid="ce-total-overhead" delay={50} />
        <StatCard label="Vehicle Tagged" value={inr(d.totals.vehicle)} accent="text-sky-600" testid="ce-total-vehicle" delay={100} />
        <StatCard label="Trip Expenses" value={inr(d.totals.trip)} accent="text-violet-600" testid="ce-total-trip" delay={150} />
      </div>
      <Tabs defaultValue="all">
        <TabsList className="flex h-auto flex-wrap">
          <TabsTrigger value="all" data-testid="ce-tab-all">All Expenses</TabsTrigger>
          <TabsTrigger value="overhead" data-testid="ce-tab-overhead">Monthly Overhead (मासिक खर्च)</TabsTrigger>
          <TabsTrigger value="vehicle" data-testid="ce-tab-vehicle">Vehicle-Wise (गाड़ी अनुसार)</TabsTrigger>
          <TabsTrigger value="trip" data-testid="ce-tab-trip">Trip-Wise (ट्रिप अनुसार)</TabsTrigger>
          <TabsTrigger value="category" data-testid="ce-tab-category">Category Summary</TabsTrigger>
          <TabsTrigger value="recurring" data-testid="ce-tab-recurring"><Repeat className="mr-1 h-3.5 w-3.5" />Recurring ({d.recurring.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="all">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <select data-testid="ce-category-filter" className="native-select h-9 w-auto" value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
              <option value="">All categories</option>{cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            {csvBtn(allRows, "Expenses", "ce-all-csv-btn")}
          </div>
          <ExpenseTable rows={allRows} testid="ce-all" {...tableProps} />
        </TabsContent>
        <TabsContent value="overhead">
          <div className="mb-3 flex justify-end">{csvBtn(d.overhead, "Monthly_Overhead", "ce-overhead-csv-btn")}</div>
          <ExpenseTable rows={d.overhead} testid="ce-overhead" {...tableProps} />
        </TabsContent>
        <TabsContent value="vehicle"><VehicleView data={d.vehicles} cats={cats} /></TabsContent>
        <TabsContent value="trip"><TripView data={d.trips} /></TabsContent>
        <TabsContent value="category"><CategoryView data={d.categories} total={d.totals.total} /></TabsContent>
        <TabsContent value="recurring"><RecurringView data={d.recurring} onEdit={(t) => setDlg({ open: true, initial: t })} onAction={recAction} /></TabsContent>
      </Tabs>
      <ExpenseDialog open={dlg.open} initial={dlg.initial} cats={cats} onClose={() => setDlg({ open: false, initial: null })} onSaved={() => { setDlg({ open: false, initial: null }); load(); }} />
    </div>
  );
}
